import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar
} from '@/components/ui/sidebar'
import { useAuth } from '@/context/AuthContext'
import { useSidebarBadges } from '@/hooks/useSidebarBadges'
import { NAV, type NavItem, type NavLeaf, flattenLeaves, isLeafActive } from '@/lib/nav'
import {
  ChevronRight,
  ChevronsUpDown,
  LogOut,
  Search,
  Settings2,
  ShieldCheck
} from 'lucide-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

function userInitials(name: string | undefined): string {
  if (!name) return '??'
  const parts = name
    .trim()
    .split(/[\s_.-]+/)
    .filter(Boolean)
  if (parts.length === 0) return name.slice(0, 2).toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}


// Default badge values — overridden at runtime by useSidebarBadges() (Phase 4+).
const DEFAULT_BADGES: Record<string, number> = {
  'moderation.pending': 0,
  'payouts.pending': 0,
  'scrapers.failed': 0,
  'bots.active': 0,
  'agents.running': 0,
  'system.failed': 0
}

function LeafButton({
  leaf,
  isSub,
  active,
  onNavigate,
  badgeValue
}: {
  leaf: NavLeaf
  isSub: boolean
  active: boolean
  onNavigate: () => void
  badgeValue?: number
}) {
  const Icon = leaf.icon
  const ButtonComp = isSub ? SidebarMenuSubButton : SidebarMenuButton
  const showBadge = typeof badgeValue === 'number' && badgeValue > 0

  return (
    <ButtonComp
      asChild
      isActive={active}
      {...(!isSub ? { tooltip: leaf.title } : {})}
    >
      <NavLink to={leaf.to} end={leaf.to === '/'} onClick={onNavigate}>
        <Icon />
        <span className="truncate">{leaf.title}</span>
        {showBadge && (
          <Badge
            variant="secondary"
            className="ml-auto h-5 min-w-5 rounded-full px-1.5 text-[10px] tabular-nums"
          >
            {badgeValue! > 99 ? '99+' : badgeValue}
          </Badge>
        )}
      </NavLink>
    </ButtonComp>
  )
}

function ParentMenu({
  item,
  pathname,
  onNavigate,
  forceOpen,
  badges
}: {
  item: Extract<NavItem, { kind: 'parent' }>
  pathname: string
  onNavigate: () => void
  forceOpen?: boolean
  badges: Record<string, number>
}) {
  const ParentIcon = item.icon
  const hasActiveChild = item.children.some((c) => isLeafActive(pathname, c))
  const [open, setOpen] = useState(
    () => forceOpen || hasActiveChild || Boolean(item.defaultOpen)
  )

  useEffect(() => {
    if (forceOpen) setOpen(true)
  }, [forceOpen])

  useEffect(() => {
    if (hasActiveChild) setOpen(true)
  }, [hasActiveChild])

  const aggregateBadge = item.children.reduce((sum, c) => {
    const v = c.badgeKey ? (badges[c.badgeKey] ?? 0) : 0
    return sum + (v > 0 ? v : 0)
  }, 0)

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="group/collapsible"
    >
      {/* An open group sits on its own soft rounded panel so it reads as one section. */}
      <SidebarMenuItem className="rounded-[24px] transition-colors group-data-[state=open]/collapsible:bg-sidebar-accent/60 group-data-[state=open]/collapsible:pb-2 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:pb-0">
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            className="rounded-full font-semibold"
          >
            <ParentIcon />
            <span className="truncate">{item.title}</span>
            {aggregateBadge > 0 && (
              <Badge
                variant="secondary"
                className="ml-auto h-5 min-w-5 rounded-full px-1.5 text-[10px] tabular-nums group-data-[collapsible=icon]:hidden"
              >
                {aggregateBadge > 99 ? '99+' : aggregateBadge}
              </Badge>
            )}
            <ChevronRight className="ml-1 size-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <SidebarMenuSub>
            {item.children.map((child, i) => (
              <Fragment key={child.to}>
              {child.section && child.section !== item.children[i - 1]?.section && (
                <li className="px-2 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50 first:pt-0.5">
                  {child.section}
                </li>
              )}
              <SidebarMenuSubItem>
                <LeafButton
                  leaf={child}
                  isSub
                  active={isLeafActive(pathname, child)}
                  onNavigate={onNavigate}
                  badgeValue={
                    child.badgeKey ? badges[child.badgeKey] : undefined
                  }
                />
              </SidebarMenuSubItem>
              </Fragment>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

export function AppSidebar() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { setOpenMobile, isMobile } = useSidebar()
  const [query, setQuery] = useState('')

  const liveBadges = useSidebarBadges()
  const badges = useMemo<Record<string, number>>(
    () => ({ ...DEFAULT_BADGES, ...liveBadges }),
    [liveBadges]
  )

  const allLeaves = useMemo(() => flattenLeaves(NAV), [])

  const trimmed = query.trim().toLowerCase()
  const searchResults = useMemo(() => {
    if (!trimmed) return null
    return allLeaves.filter((l) => {
      const inTitle = l.title.toLowerCase().includes(trimmed)
      const inParent = l.parentTitle?.toLowerCase().includes(trimmed) ?? false
      return inTitle || inParent
    })
  }, [allLeaves, trimmed])

  const handleNavClick = () => {
    if (isMobile) setOpenMobile(false)
  }

  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const first = searchResults?.[0]
      if (first) {
        navigate(first.to)
        setQuery('')
        handleNavClick()
      }
    } else if (e.key === 'Escape') {
      setQuery('')
      ;(e.target as HTMLInputElement).blur()
    }
  }

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <ShieldCheck className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">XOBLACK</span>
                <span className="truncate text-xs text-muted-foreground">
                  Super Admin
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        <div className="relative px-1 group-data-[collapsible=icon]:hidden">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <SidebarInput
            placeholder="Search admin…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKey}
            className="pl-8"
            aria-label="Search admin sections"
          />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {searchResults ? (
          <SidebarGroup>
            <SidebarGroupLabel>
              {searchResults.length === 0
                ? `No matches for "${query}"`
                : `${searchResults.length} match${searchResults.length === 1 ? '' : 'es'}`}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {searchResults.map((leaf) => (
                  <SidebarMenuItem key={leaf.to}>
                    <LeafButton
                      leaf={leaf}
                      isSub={false}
                      active={isLeafActive(location.pathname, leaf)}
                      onNavigate={() => {
                        setQuery('')
                        handleNavClick()
                      }}
                      badgeValue={
                        leaf.badgeKey ? badges[leaf.badgeKey] : undefined
                      }
                    />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1.5">
                {NAV.map((item) =>
                  item.kind === 'leaf' ? (
                    <SidebarMenuItem key={item.to}>
                      <LeafButton
                        leaf={item}
                        isSub={false}
                        active={isLeafActive(location.pathname, item)}
                        onNavigate={handleNavClick}
                        badgeValue={
                          item.badgeKey ? badges[item.badgeKey] : undefined
                        }
                      />
                    </SidebarMenuItem>
                  ) : (
                    <ParentMenu
                      key={item.title}
                      item={item}
                      pathname={location.pathname}
                      onNavigate={handleNavClick}
                      badges={badges}
                    />
                  )
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent"
                >
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xs font-semibold">
                      {userInitials(user?.username)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">
                      {user?.username ?? '—'}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user?.email ?? ''}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side={isMobile ? 'bottom' : 'right'}
                align="end"
                sideOffset={8}
                className="w-56"
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-0.5">
                    <span className="text-sm font-medium">
                      {user?.username}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate('/controls/roles')}>
                  <Settings2 />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={signOut} variant="destructive">
                  <LogOut />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
